import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import {
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  Col,
  Input,
  InputGroup,
  InputGroupText,
  Row,
  Table,
} from "reactstrap";
import api from "../../config/api";
import { useAppDialog } from "./AppDialogProvider";

const REFRESH_INTERVAL_MS = 60000;

export default function UncheckedParts({ onBack, onOpenConfirmPart }) {
  const dialog = useAppDialog();

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [selectedRowKey, setSelectedRowKey] = useState("");

  const searchInputRef = useRef(null);
  const rowRefs = useRef({});
  const mountedRef = useRef(true);

  const selectedRow = useMemo(() => {
    return rows.find((row) => getRowKey(row) === selectedRowKey) || null;
  }, [rows, selectedRowKey]);

  const displayRows = useMemo(() => {
    const search = searchText.trim().toUpperCase();
    if (!search) return rows;

    return rows.filter((row) => {
      const partNumber = String(row.partNumber ?? "").toUpperCase();
      const printRevision = String(row.printRevision ?? "").toUpperCase();
      const mafiaRevision = String(row.mafiaRevision ?? "").toUpperCase();
      const by = String(row.by ?? "").toUpperCase();
      const timeStamp = formatDateTime(row.timeStamp).toUpperCase();

      return (
        partNumber.includes(search) ||
        printRevision.includes(search) ||
        mafiaRevision.includes(search) ||
        by.includes(search) ||
        timeStamp.includes(search)
      );
    });
  }, [rows, searchText]);

  const refreshData = useCallback(
    async (showSpinner = true) => {
      try {
        if (showSpinner) {
          setRefreshing(true);
        }

        const data = await getUncheckedParts();
        if (!mountedRef.current) return;

        const safeData = data || [];
        setRows(safeData);

        if (selectedRowKey) {
          const stillExists = safeData.some((row) => getRowKey(row) === selectedRowKey);
          if (!stillExists) {
            setSelectedRowKey("");
          }
        }
      } catch (error) {
        console.error("Failed to refresh unchecked parts:", error);
        if (showSpinner) {
          await dialog.error("Failed to refresh unchecked parts.");
        }
      } finally {
        if (mountedRef.current && showSpinner) {
          setRefreshing(false);
        }
      }
    },
    [selectedRowKey, dialog]
  );

  useEffect(() => {
    mountedRef.current = true;

    async function loadInitial() {
      try {
        const data = await getUncheckedParts();
        if (!mountedRef.current) return;
        setRows(data || []);
      } catch (error) {
        console.error("Failed to load unchecked parts:", error);
        await dialog.error("Failed to load unchecked parts list.");
      } finally {
        if (mountedRef.current) {
          setLoading(false);
          if (searchInputRef.current) {
            searchInputRef.current.focus();
            searchInputRef.current.select();
          }
        }
      }
    }

    loadInitial();

    return () => {
      mountedRef.current = false;
    };
  }, [dialog]);

  useEffect(() => {
    const timerId = window.setInterval(() => {
      refreshData(false);
    }, REFRESH_INTERVAL_MS);

    return () => window.clearInterval(timerId);
  }, [refreshData]);

  function handleRowSelect(row) {
    if (!row) return;
    setSelectedRowKey(getRowKey(row));
  }

  async function openConfirmPart(row = selectedRow) {
    if (!row?.partNumber) {
      await dialog.warning("Please select a part first.");
      return;
    }

    try {
      if (typeof onOpenConfirmPart === "function") {
        onOpenConfirmPart({
          partNum: String(row.partNumber ?? ""),
          rev: Number(row.printRevision ?? 0),
          revMinor: Number(row.mafiaRevision ?? 0),
        });
      } else {
        console.warn("onOpenConfirmPart callback is not configured.");
        await dialog.error("Confirm Part page is not configured.");
      }
    } catch (error) {
      console.error("Failed to open confirm part:", error);
      await dialog.error("Failed to open confirm part.");
    }
  }

  function handleSearchKeyDown(e) {
    if (e.key !== "Enter") return;

    if (displayRows.length > 0) {
      const firstMatch = displayRows[0];
      const key = getRowKey(firstMatch);
      setSelectedRowKey(key);

      const rowEl = rowRefs.current[key];
      if (rowEl) {
        rowEl.scrollIntoView({ block: "nearest", behavior: "smooth" });
      }
    }

    if (searchInputRef.current) {
      searchInputRef.current.select();
    }
  }

  return (
    <div className="unchecked-parts-page">
      <Row className="g-4">
        <Col xl={12}>
          <Card className="border-0 shadow-sm">
            <CardHeader className="bg-white border-0 pt-4 px-4 pb-3">
              <div className="unchecked-parts-toolbar-main">
                <div className="unchecked-parts-toolbar-main-left">
                  <Button
                    color="soft-dark"
                    className="unchecked-parts-action-btn"
                    onClick={onBack}
                  >
                    <i className="ri-arrow-left-line unchecked-parts-btn-icon me-1" />
                    Back
                  </Button>

                  <Button
                    color="success"
                    className="unchecked-parts-action-btn"
                    onClick={() => openConfirmPart()}
                    disabled={!selectedRow}
                  >
                    <i className="ri-checkbox-circle-line unchecked-parts-btn-icon me-1" />
                    Open Confirm Part
                  </Button>
                </div>

                <div className="unchecked-parts-toolbar-main-right">
                  <InputGroup className="unchecked-parts-search-group">
                    <InputGroupText className="bg-light border-end-0">
                      <i className="ri-search-line" />
                    </InputGroupText>
                    <Input
                      innerRef={searchInputRef}
                      type="text"
                      value={searchText}
                      onChange={(e) => setSearchText(e.target.value)}
                      onKeyDown={handleSearchKeyDown}
                      className="border-start-0"
                      placeholder="Search part number, revision, or user"
                    />
                  </InputGroup>
                </div>
              </div>

              <div className="unchecked-parts-toolbar-utility">
                <Button
                  color="primary"
                  className="unchecked-parts-action-btn unchecked-parts-utility-btn"
                  onClick={() => refreshData(true)}
                  disabled={refreshing}
                >
                  <i className="ri-refresh-line unchecked-parts-btn-icon me-1" />
                  {refreshing ? "Refreshing..." : "Refresh"}
                </Button>

                <Badge color="light" className="unchecked-parts-queue-badge">
                  Queue Size: {rows.length}
                </Badge>

                <div className="unchecked-parts-info-pill">
                  <span className="unchecked-parts-info-label">Selected Part</span>
                  <span
                    className="unchecked-parts-info-value"
                    title={selectedRow?.partNumber || "No selection"}
                  >
                    {selectedRow?.partNumber || "No selection"}
                  </span>
                </div>

                <div className="unchecked-parts-info-pill">
                  <span className="unchecked-parts-info-label">Revision</span>
                  <span className="unchecked-parts-info-value">
                    {selectedRow
                      ? `${selectedRow.printRevision ?? 0}.${selectedRow.mafiaRevision ?? 0}`
                      : "—"}
                  </span>
                </div>

                <div className="unchecked-parts-refresh-note">
                  Auto refresh every 1 min
                </div>
              </div>
            </CardHeader>

            <CardBody className="p-4">
              <div className="unchecked-parts-table-shell">
                <div className="unchecked-parts-table-scroll">
                  <Table responsive={false} className="align-middle mb-0 unchecked-parts-table">
                    <thead>
                      <tr>
                        <th className="unchecked-parts-col-select">Select</th>
                        <th>Part Number</th>
                        <th>Print Revision</th>
                        <th>MAFIA Revision</th>
                        <th>By</th>
                        <th>Timestamp</th>
                      </tr>
                    </thead>
                    <tbody>
                      {loading ? (
                        <tr>
                          <td colSpan={6} className="text-center py-5 text-muted">
                            Loading unchecked parts...
                          </td>
                        </tr>
                      ) : displayRows.length === 0 ? (
                        <tr>
                          <td colSpan={6} className="text-center py-5 text-muted">
                            No unchecked parts found.
                          </td>
                        </tr>
                      ) : (
                        displayRows.map((row) => {
                          const rowKey = getRowKey(row);
                          const isSelected = rowKey === selectedRowKey;

                          return (
                            <tr
                              key={rowKey}
                              ref={(el) => {
                                if (el) {
                                  rowRefs.current[rowKey] = el;
                                }
                              }}
                              tabIndex={0}
                              onClick={() => handleRowSelect(row)}
                              onDoubleClick={() => openConfirmPart(row)}
                              className={`unchecked-parts-data-row ${isSelected ? "selected" : ""}`}
                            >
                              <td className="unchecked-parts-col-select">
                                <Input
                                  type="radio"
                                  checked={isSelected}
                                  onChange={() => handleRowSelect(row)}
                                  name="selected-unchecked-part-row"
                                />
                              </td>
                              <td>
                                <div className="fw-semibold text-dark">{row.partNumber}</div>
                              </td>
                              <td>{row.printRevision}</td>
                              <td>{row.mafiaRevision}</td>
                              <td>{row.by}</td>
                              <td>{formatDateTime(row.timeStamp)}</td>
                            </tr>
                          );
                        })
                      )}
                    </tbody>
                  </Table>
                </div>
              </div>
            </CardBody>
          </Card>
        </Col>
      </Row>

      <style>{`
        .unchecked-parts-toolbar-main {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 1rem;
          flex-wrap: wrap;
          margin-bottom: 0.9rem;
        }

        .unchecked-parts-toolbar-main-left {
          display: flex;
          align-items: center;
          gap: 0.75rem;
          flex-wrap: wrap;
        }

        .unchecked-parts-toolbar-main-right {
          display: flex;
          align-items: center;
          justify-content: flex-end;
          flex: 0 0 360px;
          min-width: 280px;
        }

        .unchecked-parts-toolbar-utility {
          display: flex;
          align-items: center;
          gap: 0.75rem;
          flex-wrap: wrap;
          padding-top: 0.25rem;
        }

        .unchecked-parts-search-group {
          width: 100%;
        }

        .unchecked-parts-action-btn {
          border-radius: 0.65rem;
          font-weight: 600;
          padding: 0.42rem 0.8rem;
          font-size: 0.9rem;
          white-space: nowrap;
        }

        .unchecked-parts-utility-btn {
          min-width: 110px;
        }

        .unchecked-parts-btn-icon {
          font-size: 0.95rem;
        }

        .unchecked-parts-queue-badge {
          display: inline-flex;
          align-items: center;
          min-height: 38px;
          border: 1px solid rgba(15, 23, 42, 0.08);
          padding: 0.58rem 0.8rem;
          border-radius: 0.65rem;
          font-size: 0.85rem;
          color: #0f172a;
          background: #f8fafc;
        }

        .unchecked-parts-info-pill {
          display: inline-flex;
          align-items: center;
          gap: 0.45rem;
          min-height: 38px;
          padding: 0.45rem 0.75rem;
          border: 1px solid rgba(15, 23, 42, 0.08);
          border-radius: 0.65rem;
          background: #f8fafc;
          max-width: 300px;
        }

        .unchecked-parts-info-label {
          font-size: 0.72rem;
          font-weight: 700;
          text-transform: uppercase;
          color: #64748b;
          white-space: nowrap;
        }

        .unchecked-parts-info-value {
          font-size: 0.9rem;
          font-weight: 700;
          color: #0f172a;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }

        .unchecked-parts-refresh-note {
          min-height: 38px;
          display: inline-flex;
          align-items: center;
          padding: 0.45rem 0.2rem;
          font-size: 0.84rem;
          color: #64748b;
          font-weight: 500;
        }

        .btn-soft-dark {
          background: rgba(15, 23, 42, 0.08);
          color: #0f172a;
          border: 1px solid rgba(15, 23, 42, 0.08);
        }

        .btn-soft-dark:hover,
        .btn-soft-dark:focus {
          background: rgba(15, 23, 42, 0.14);
          color: #0f172a;
          border-color: rgba(15, 23, 42, 0.12);
        }

        .unchecked-parts-table-shell {
          border: 1px solid rgba(15, 23, 42, 0.08);
          border-radius: 1rem;
          overflow: hidden;
          background: #fff;
        }

        .unchecked-parts-table-scroll {
          max-height: 540px;
          overflow: auto;
        }

        .unchecked-parts-table {
          min-width: 980px;
        }

        .unchecked-parts-table thead th {
          position: sticky;
          top: 0;
          z-index: 2;
          background: #0f172a;
          color: #f8fafc;
          font-size: 0.74rem;
          text-transform: uppercase;
          letter-spacing: 0.08em;
          font-weight: 800;
          padding: 0.95rem 0.9rem;
          border-bottom: 0;
          white-space: nowrap;
        }

        .unchecked-parts-table tbody td {
          padding: 0.9rem 0.9rem;
          border-top: 1px solid #eef2f7;
          color: #334155;
          vertical-align: middle;
          white-space: nowrap;
        }

        .unchecked-parts-col-select {
          width: 72px;
          text-align: center;
        }

        .unchecked-parts-data-row {
          transition: background-color 0.18s ease, box-shadow 0.18s ease;
          cursor: pointer;
        }

        .unchecked-parts-data-row:nth-of-type(even) {
          background: #fbfcfe;
        }

        .unchecked-parts-data-row:hover {
          background: #f5f9ff;
        }

        .unchecked-parts-data-row.selected {
          background: rgba(13, 110, 253, 0.08);
          box-shadow: inset 4px 0 0 #0d6efd;
        }

        @media (max-width: 1199.98px) {
          .unchecked-parts-toolbar-main {
            flex-direction: column;
            align-items: stretch;
          }

          .unchecked-parts-toolbar-main-right {
            flex: 1 1 auto;
            min-width: 100%;
          }
        }

        @media (max-width: 991.98px) {
          .unchecked-parts-table-scroll {
            max-height: 460px;
          }

          .unchecked-parts-info-pill {
            max-width: 100%;
          }

          .unchecked-parts-toolbar-utility {
            align-items: stretch;
          }
        }
      `}</style>
    </div>
  );
}

function getRowKey(row) {
  return `${row.partNumber ?? ""}-${row.printRevision ?? 0}-${row.mafiaRevision ?? 0}`;
}

function formatDateTime(value) {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleString();
}

async function getUncheckedParts() {
  const response = await api.get("mafia/unchecked-parts");

  return (response.data || []).map((item) => ({
    partNumber: item.partNumber,
    printRevision: item.printRevision,
    mafiaRevision: item.mafiaRevision,
    by: item.by,
    timeStamp: item.timeStamp,
  }));
}